import { useCallback, useEffect, useState } from 'react';
import { EmployeeInput, EmployeePatch, PositionInput, type AreaDto, type EmployeeDto, type LoginAttemptDto, type PositionDto, type Role } from '@app/shared';
import { api } from '../../api';
import { platform } from '../../platform';
import { useApp } from '../../state';
import { Icon, Sheet, when } from '../../ui';
import { areaPath } from '../Panels';
import { SubHead } from './SubHead';
import { roleLine } from '../TopBar';

type Draft = { employee: EmployeeDto | null; preset?: { fullName: string; telegram: string } };

/** Сотрудники: вход в приложение только для тех, кто в этом списке. */
export function Staff() {
  const { fail, reloadMe, me } = useApp();
  const [list, setList] = useState<EmployeeDto[] | null>(null);
  const [attempts, setAttempts] = useState<LoginAttemptDto[]>([]);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [positions, setPositions] = useState<PositionDto[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [posOpen, setPosOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, a, t, p] = await Promise.all([api.staff(), api.areas(), api.loginAttempts(), api.positions()]);
      setList(s);
      setAreas(a);
      setAttempts(t);
      setPositions(p);
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => void load(), [load]);

  const active = list?.filter((e) => e.status === 'active') ?? [];
  const blocked = list?.filter((e) => e.status === 'blocked') ?? [];

  const row = (e: EmployeeDto) => (
    <button key={e.id} type="button" className="item" onClick={() => setDraft({ employee: e })}>
      <span className="grow stack" style={{ gap: 2 }}>
        <span className="title ellipsis">
          {e.fullName}
          {e.id === me.employee?.id ? ' (вы)' : ''}
        </span>
        <span className="sub ellipsis">
          {roleLine(e)}
          {e.role === 'admin' ? ' · все участки' : e.areaIds.length ? ` · ${e.areaIds.map((id) => areaPath(areas, id)).join(', ')}` : ' · участки не назначены'}
        </span>
        <span className="sub ellipsis">
          {e.lastSeenAt ? `входил ${when(e.lastSeenAt)}` : 'ещё не входил'}
          {e.tgUsername ? ` · @${e.tgUsername}` : e.userId ? ` · ID ${e.userId}` : ''}
          {e.phone ? ` · +${e.phone}` : ''}
        </span>
      </span>
      {!e.lastSeenAt && e.status === 'active' && <span className="badge outline">ждём входа</span>}
      <span className="chev">
        <Icon name="right" size={18} />
      </span>
    </button>
  );

  return (
    <div className="stack">
      <SubHead title="Сотрудники">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setDraft({ employee: null })}>
          <Icon name="plus" size={16} />
          Добавить
        </button>
      </SubHead>
      <div className="card flat small">Войти могут только сотрудники из этого списка. Добавьте человека по Telegram ID, @username или телефону — при первом входе запись привяжется к его Telegram.</div>

      <button type="button" className="item" onClick={() => setPosOpen(true)}>
        <span className="mark">
          <Icon name="users" />
        </span>
        <span className="grow stack" style={{ gap: 2 }}>
          <span className="title">Должности · {positions.length}</span>
          <span className="sub ellipsis">Кто кому подчиняется. Замечания из проверок уходят руководителю.</span>
        </span>
        <span className="chev">
          <Icon name="right" size={18} />
        </span>
      </button>

      {attempts.length > 0 && (
        <>
          <h2 className="section-title">Пытались войти · {attempts.length}</h2>
          <div className="list">
            {attempts.map((a) => {
              const name = [a.lastName, a.firstName].filter(Boolean).join(' ');
              return (
                <div key={a.userId} className="item">
                  <span className="grow stack" style={{ gap: 2 }}>
                    <span className="title ellipsis">{name}</span>
                    <span className="sub ellipsis">
                      {a.username ? `@${a.username} · ` : ''}ID {a.userId} · {when(a.lastSeenAt)}
                    </span>
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft({ employee: null, preset: { fullName: name, telegram: String(a.userId) } })}>
                    Добавить
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!list && <div className="skeleton" />}
      {active.length > 0 && (
        <>
          <h2 className="section-title">В списке · {active.length}</h2>
          <div className="list">{active.map(row)}</div>
        </>
      )}
      {blocked.length > 0 && (
        <>
          <h2 className="section-title">Доступ закрыт · {blocked.length}</h2>
          <div className="list">{blocked.map(row)}</div>
        </>
      )}
      {draft && (
        <EmployeeSheet
          draft={draft}
          areas={areas}
          positions={positions}
          isSelf={draft.employee?.id === me.employee?.id}
          onClose={() => setDraft(null)}
          onSaved={async () => {
            setDraft(null);
            await Promise.all([load(), reloadMe()]);
          }}
        />
      )}
      {posOpen && <PositionsSheet positions={positions} onClose={() => setPosOpen(false)} onChanged={load} />}
    </div>
  );
}

/** Должности деревом: «Начальник производства → Мастер смены → Оператор». */
export function positionTree(positions: PositionDto[]): { p: PositionDto; depth: number }[] {
  const ids = new Set(positions.map((p) => p.id));
  const out: { p: PositionDto; depth: number }[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const p of positions.filter((x) => (x.parentId && ids.has(x.parentId) ? x.parentId : null) === parent)) {
      if (out.some((o) => o.p.id === p.id) || depth > 20) continue;
      out.push({ p, depth });
      walk(p.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

function PositionsSheet({ positions, onClose, onChanged }: { positions: PositionDto[]; onClose: () => void; onChanged: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [edit, setEdit] = useState<PositionDto | 'new' | null>(null);
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [busy, setBusy] = useState(false);
  const tree = positionTree(positions);

  function start(p: PositionDto | 'new') {
    setEdit(p);
    setName(p === 'new' ? '' : p.name);
    setParentId(p === 'new' ? '' : (p.parentId ?? ''));
  }

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await fn();
      toast(done);
      setEdit(null);
      await onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  function save() {
    const body = { name, parentId: parentId || null };
    const p = PositionInput.safeParse(body);
    if (!p.success) return fail(new Error(p.error.issues[0]?.message ?? 'Проверьте поля'));
    void run(() => (edit === 'new' ? api.createPosition(body) : api.updatePosition((edit as PositionDto).id, body)), 'Должность сохранена');
  }

  if (edit) {
    const self = edit === 'new' ? null : edit;
    // Подчинить можно любой должности, кроме самой себя и своих подчинённых.
    const below = new Set<string>();
    if (self) {
      const stack = [self.id];
      while (stack.length) {
        const cur = stack.pop()!;
        below.add(cur);
        stack.push(...positions.filter((x) => x.parentId === cur).map((x) => x.id));
      }
    }
    return (
      <Sheet title={self ? self.name : 'Новая должность'} onClose={() => setEdit(null)}>
        <div className="field">
          <label htmlFor="pn">Название</label>
          <input id="pn" className="input" value={name} onChange={(ev) => setName(ev.target.value)} placeholder="Мастер смены" />
        </div>
        <div className="field">
          <label htmlFor="pp">Подчиняется</label>
          <select id="pp" className="input" value={parentId} onChange={(ev) => setParentId(ev.target.value)}>
            <option value="">— никому (верх иерархии)</option>
            {tree
              .filter(({ p }) => !below.has(p.id))
              .map(({ p, depth }) => (
                <option key={p.id} value={p.id}>
                  {'\u00a0\u00a0'.repeat(depth)}
                  {p.name}
                </option>
              ))}
          </select>
          <span className="hint">Замечания, найденные при проверке оборудования, уходят тому, кому подчиняется сотрудник.</span>
        </div>
        <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={save}>
          Сохранить
        </button>
        {self && (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            disabled={busy || self.count > 0}
            onClick={() => void run(() => api.deletePosition(self.id), 'Должность удалена')}
          >
            {self.count > 0 ? `Удалить нельзя: на должности ${self.count} чел.` : 'Удалить должность'}
          </button>
        )}
      </Sheet>
    );
  }

  return (
    <Sheet title="Должности" onClose={onClose}>
      <p className="small muted" style={{ margin: 0 }}>
        Должность — место в иерархии компании. Права в приложении даёт не она, а отметка «Администратор» у сотрудника.
      </p>
      <div className="list">
        {tree.map(({ p, depth }) => (
          <button key={p.id} type="button" className="item" style={{ paddingLeft: 16 + depth * 18 }} onClick={() => start(p)}>
            <span className="grow stack" style={{ gap: 2 }}>
              <span className="title ellipsis">
                {depth > 0 && <span className="muted">└ </span>}
                {p.name}
              </span>
              <span className="sub">{p.count > 0 ? `${p.count} чел.` : 'никого'}</span>
            </span>
            <span className="chev">
              <Icon name="right" size={18} />
            </span>
          </button>
        ))}
      </div>
      <button type="button" className="btn btn-primary btn-block" onClick={() => start('new')}>
        <Icon name="plus" size={16} />
        Добавить должность
      </button>
    </Sheet>
  );
}

function EmployeeSheet({ draft, areas, positions, isSelf, onClose, onSaved }: { draft: Draft; areas: AreaDto[]; positions: PositionDto[]; isSelf: boolean; onClose: () => void; onSaved: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const e = draft.employee;
  const linked = !!e?.lastSeenAt;
  const [fullName, setFullName] = useState(e?.fullName ?? draft.preset?.fullName ?? '');
  const [positionId, setPositionId] = useState(e?.positionId ?? '');
  const [telegram, setTelegram] = useState(draft.preset?.telegram ?? (e ? (e.userId ? String(e.userId) : e.tgUsername ? `@${e.tgUsername}` : '') : ''));
  const [phone, setPhone] = useState(e?.phone ? `+${e.phone}` : '');
  const [role, setRole] = useState<Role>(e?.role ?? 'employee');
  const [areaIds, setAreaIds] = useState<string[]>(e?.areaIds ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allAreas = role === 'admin';

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      platform.haptic('success');
      toast(done);
      await onSaved();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  function save() {
    const contact = linked ? {} : { telegram: telegram.trim() || null, phone: phone.trim() || null };
    if (!e) {
      const p = EmployeeInput.safeParse({ fullName, positionId: positionId || null, role, areaIds, ...contact });
      if (!p.success) return setError(p.error.issues[0]?.message ?? 'Проверьте поля');
      return void run(() => api.createEmployee({ fullName, positionId: positionId || null, role, areaIds, ...contact }), 'Сотрудник добавлен');
    }
    const patch = { fullName, positionId: positionId || null, areaIds, ...(isSelf ? {} : { role }), ...contact };
    const p = EmployeePatch.safeParse(patch);
    if (!p.success) return setError(p.error.issues[0]?.message ?? 'Проверьте поля');
    void run(() => api.patchEmployee(e.id, patch), 'Сохранено');
  }

  const toggleArea = (id: string) => setAreaIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const sortedAreas = [...areas].sort((a, b) => areaPath(areas, a.id).localeCompare(areaPath(areas, b.id), 'ru'));

  return (
    <Sheet title={e ? e.fullName : 'Новый сотрудник'} onClose={onClose}>
      <div className="field">
        <label htmlFor="sf">Фамилия и имя</label>
        <input id="sf" className="input" value={fullName} onChange={(ev) => setFullName(ev.target.value)} placeholder="Иванов Иван" />
      </div>
      <div className="field">
        <label htmlFor="sp">Должность</label>
        <select id="sp" className="input" value={positionId} onChange={(ev) => setPositionId(ev.target.value)}>
          <option value="">— не указана</option>
          {positionTree(positions).map(({ p, depth }) => (
            <option key={p.id} value={p.id}>
              {'\u00a0\u00a0'.repeat(depth)}
              {p.name}
            </option>
          ))}
        </select>
        <span className="hint">Нужной нет — добавьте в «Должности» на экране сотрудников.</span>
      </div>
      {linked ? (
        <div className="small muted">
          Telegram привязан: {e!.tgUsername ? `@${e!.tgUsername} · ` : ''}ID {e!.userId}
          {e!.phone ? ` · +${e!.phone}` : ''}
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="st">Telegram: ID или @username</label>
            <input id="st" className="input" value={telegram} autoCapitalize="off" autoCorrect="off" onChange={(ev) => setTelegram(ev.target.value)} placeholder="1014614490 или @ivanov" />
            <span className="hint">ID надёжнее: @username человек может сменить. ID видно на экране входа у самого сотрудника.</span>
          </div>
          <div className="field">
            <label htmlFor="sph">Или телефон</label>
            <input id="sph" className="input" value={phone} inputMode="tel" onChange={(ev) => setPhone(ev.target.value)} placeholder="+7 916 123-45-67" />
            <span className="hint">Сотрудник войдёт кнопкой «Войти по номеру телефона» — Telegram подтвердит, что номер его.</span>
          </div>
        </>
      )}

      <div className="field">
        <label className="check">
          <input type="checkbox" checked={role === 'admin'} disabled={isSelf} onChange={(ev) => setRole(ev.target.checked ? 'admin' : 'employee')} />
          <span className="stack" style={{ gap: 2 }}>
            <span>Администратор приложения</span>
            <span className="hint">
              {isSelf
                ? 'Свою роль меняет другой администратор.'
                : 'Видит всё, заводит оборудование, панели и сотрудников, смотрит журнал. Без отметки — работает с оборудованием своих участков.'}
            </span>
          </span>
        </label>
      </div>

      <div className="field">
        <span className="label">Какое оборудование видит</span>
        {allAreas ? (
          <span className="hint">Администратор видит все участки.</span>
        ) : areas.length === 0 ? (
          <span className="hint">Участков ещё нет — создайте их в разделе «Оборудование».</span>
        ) : (
          <div className="stack" style={{ gap: 2 }}>
            {sortedAreas.map((a) => (
              <label key={a.id} className="check">
                <input type="checkbox" checked={areaIds.includes(a.id)} onChange={() => toggleArea(a.id)} />
                <span>{areaPath(areas, a.id)}</span>
              </label>
            ))}
            <span className="hint">Вложенные участки открываются вместе с родительским. Открыл панель — может ей управлять.</span>
          </div>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={save}>
        {e ? 'Сохранить' : 'Добавить в список'}
      </button>
      {e && !isSelf && (
        <button
          type="button"
          className="btn btn-danger btn-block"
          disabled={busy}
          onClick={() =>
            e.status === 'blocked'
              ? void run(() => api.patchEmployee(e.id, { status: 'active' }), 'Доступ возвращён')
              : void platform
                  .confirm(`Закрыть доступ: ${e.fullName}? Его открытые подключения к панелям оборвутся сразу.`)
                  .then((ok) => {
                    if (ok) void run(() => api.patchEmployee(e.id, { status: 'blocked' }), 'Доступ закрыт');
                  })
          }
        >
          {e.status === 'blocked' ? 'Вернуть доступ' : 'Закрыть доступ'}
        </button>
      )}
      {e && !isSelf && !e.lastSeenAt && (
        <button type="button" className="btn btn-ghost btn-block" disabled={busy} onClick={() => void run(() => api.deleteEmployee(e.id), 'Удалён из списка')}>
          Удалить из списка
        </button>
      )}
    </Sheet>
  );
}
